import ToggleButton from "../../../../../Components/common/ToggleButton/ToggleButton";
import CustomSelect from "../../../../../Components/common/CustomSelect/CustomSelect";
import Tooltip from "../../../../../Components/common/Tooltip/Tooltip";
import Field from "../../Components/Field";
import { LLM_PROVIDERS, DEFAULT_AZURE_API_VERSION, getProvider } from "./llmProviders";

import "./styles.scss";

const PROVIDER_OPTIONS = LLM_PROVIDERS.map((p) => ({ id: p.key, value: p.label }));

// Provider, connection and advanced fields of a customer LLM. Used by the LLM settings
// page and the Phishbox LLM tab; the parent owns the state and how it is saved.
// onChange receives the changed fields as an object.
const LlmConfigFields = ({ configs, onChange, onProviderChange, hasStoredKey }) => {
  const provider = getProvider(configs.provider);
  const isAzure = configs.provider === "azure";
  const isCustom = configs.provider === "custom";

  const inputChangeHandler = (e) => {
    const { name, value } = e.target;
    onChange({ [name]: value });
  };

  // How the backend sends the key (llmutils.build_llm_target)
  const customHeader = configs.auth_header.trim();
  const usesBearer =
    !isAzure && (!isCustom || !customHeader || customHeader.toLowerCase() === "authorization");
  const sentHeaderName = isAzure ? "api-key" : usesBearer ? "Authorization" : customHeader;
  const sentHeader = `${sentHeaderName}: ${usesBearer ? "Bearer " : ""}<API key>`;

  const apiKeyChangeHandler = (e) => {
    let { value } = e.target;
    // "Bearer" is already shown as a prefix, drop a pasted one
    if (usesBearer && /^bearer\s+/i.test(value)) {
      value = value.replace(/^bearer\s+/i, "");
    }
    onChange({ api_key: value });
  };

  return (
    <>
      <div className="llm-settings__line">
        <div>
          <p className="settings__label">
            Provider
            <Tooltip text="LLM provider. Every provider is used through its OpenAI compatible chat completions API." />
          </p>
          <div className="llm-settings__select">
            <CustomSelect
              data={PROVIDER_OPTIONS}
              select={PROVIDER_OPTIONS.find((item) => item.id === configs.provider)}
              setSelected={(item) => item && item.id !== configs.provider && onProviderChange(item)}
              width="318px"
              height="38px"
            />
          </div>
        </div>
      </div>

      <div className="llm-settings__line">
        <Field
          label={isAzure || isCustom ? "Endpoint URL*" : "Base URL*"}
          tooltipText={
            isAzure
              ? "Azure OpenAI resource endpoint, without the /openai/deployments path."
              : isCustom
                ? "Full chat completions endpoint of your OpenAI compatible service. It is used as entered."
                : "OpenAI compatible API base URL as documented by the provider. /chat/completions is appended unless the URL already ends with it."
          }
          width="430px"
          name="base_url"
          value={configs.base_url}
          onChange={inputChangeHandler}
          placeholder={provider.baseUrlPlaceholder || provider.defaultBaseUrl}
        />
      </div>
      {configs.provider === "ollama" && (
        <p className="llm-settings__hint">
          The URL is resolved by the CyberFortress server, so use the address of the Ollama host as
          seen from the server instead of localhost when it runs elsewhere.
        </p>
      )}

      {isCustom && (
        <div className="llm-settings__line">
          <Field
            label="Auth Header"
            tooltipText='Header that carries the API key. "Authorization" sends it as "Bearer <API key>", any other header sends the raw key.'
            width="318px"
            name="auth_header"
            value={configs.auth_header}
            onChange={inputChangeHandler}
            placeholder="Authorization"
          />
        </div>
      )}

      <div className="llm-settings__line llm-settings__line--column">
        <p className="settings__label">
          {provider.keyRequired ? "API Key*" : "API Key"}
          <Tooltip
            text={
              hasStoredKey
                ? "An API key is already saved. Leave empty to keep the current key."
                : "API key used to authenticate with the provider."
            }
          />
        </p>
        <div className="llm-settings__key">
          {usesBearer && <span className="llm-settings__key-prefix">Bearer</span>}
          <input
            className="settings__field"
            name="api_key"
            type="password"
            value={configs.api_key}
            onChange={apiKeyChangeHandler}
            placeholder={hasStoredKey ? "Leave empty to keep current key" : "Enter API key"}
            autoComplete="new-password"
          />
        </div>
        <p className="llm-settings__key-hint">
          Sent as header: <code>{sentHeader}</code>
        </p>
      </div>

      {isAzure && (
        <div className="llm-settings__line">
          <Field
            parentClassName="p-r-20"
            label="Deployment Name*"
            tooltipText="Name of the model deployment in your Azure OpenAI resource."
            width="318px"
            name="azure_deployment"
            value={configs.azure_deployment}
            onChange={inputChangeHandler}
            placeholder="gpt-4o-deployment"
          />
          <Field
            label="API Version"
            tooltipText="Azure OpenAI REST API version."
            width="160px"
            name="azure_api_version"
            value={configs.azure_api_version}
            onChange={inputChangeHandler}
            placeholder={DEFAULT_AZURE_API_VERSION}
          />
        </div>
      )}

      <div className="llm-settings__line llm-settings__line--column">
        <Field
          label="Model"
          tooltipText={
            isAzure
              ? "Optional, the deployment decides the model."
              : provider.defaultModel
                ? `Optional. Model name exactly as the provider expects it. ${provider.defaultModel} is used when left empty.`
                : "Optional. Model name exactly as the provider expects it. The provider's default model is used when left empty."
          }
          width="318px"
          name="model"
          value={configs.model}
          onChange={inputChangeHandler}
          placeholder={
            provider.defaultModel ? `Default: ${provider.defaultModel}` : "Provider default"
          }
        />
        {provider.modelSuggestions.length > 0 && (
          <div className="llm-settings__suggestions">
            {provider.modelSuggestions.map((model) => (
              <button
                key={model}
                type="button"
                className={configs.model === model ? "active" : ""}
                onClick={() => onChange({ model })}>
                {model}
              </button>
            ))}
          </div>
        )}
      </div>

      <h4 className="llm-settings__section-title">Advanced</h4>
      <div className="llm-settings__line">
        <Field
          parentClassName="p-r-20"
          label="Temperature"
          tooltipText="0 - 2. Leave empty to use the provider default."
          width="110px"
          name="temperature"
          value={configs.temperature}
          onChange={inputChangeHandler}
          placeholder="default"
        />
        <Field
          parentClassName="p-r-20"
          label="Max Tokens"
          tooltipText="Maximum tokens in a response. Leave empty to use the provider default."
          width="110px"
          name="max_tokens"
          value={configs.max_tokens}
          onChange={inputChangeHandler}
          placeholder="default"
        />
        <Field
          label="Timeout (sec)"
          tooltipText="Timeout duration (in seconds) for LLM requests."
          width="110px"
          name="timeout"
          value={configs.timeout}
          onChange={inputChangeHandler}
          placeholder="400"
        />
      </div>
      <div className="llm-settings__line">
        <ToggleButton
          active={configs.verify_ssl}
          onClick={(verify_ssl) => onChange({ verify_ssl })}
          rightText="Verify SSL certificate"
        />
      </div>
    </>
  );
};

export default LlmConfigFields;
